import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Sprout, Star, Calendar, Thermometer, Droplets, TrendingUp, ShoppingCart, ExternalLink, Info } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RecommendedVarieties({ user }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const res = await axios.get(`${API}/varieties/recommended`, { withCredentials: true });
      setRecommendations(res.data.recommendations || []);
      setCurrentMonth(res.data.current_month || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Recommended Varieties
          </h1>
          <p className="text-base text-[#5C665C] mt-2">
            Expert picks for your garden, location and season — {currentMonth}
          </p>
        </div>

        {/* Hero Banner */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#2D5F44] rounded-3xl p-8 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Star className="w-8 h-8 text-[#B4D330]" />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Homegrown Picks for {currentMonth}
            </h2>
          </div>
          <p className="text-[#B4D330]/90 text-base max-w-3xl">
            Based on your garden, location, season and planting plan, these are the varieties we recommend you plant now.
          </p>
        </div>

        {/* Recommendations Grid */}
        {recommendations.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center">
            <Sprout className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#2D241E] mb-2">No recommendations yet</h3>
            <p className="text-sm text-[#5C665C]">Add crops to your garden planner to get personalized variety recommendations.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {recommendations.map((variety) => (
              <VarietyCard
                key={variety.variety_id}
                variety={variety}
                onClick={() => setSelectedVariety(variety)}
              />
            ))}
          </div>
        )}

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-[#1A4331]/5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#1A4331]/10 flex items-center justify-center">
                <Star className="w-5 h-5 text-[#1A4331]" />
              </div>
              <h3 className="font-semibold text-[#1A4331]">Homegrown Recommended</h3>
            </div>
            <p className="text-sm text-[#5C665C]">
              These varieties are selected for home gardeners — not commercial growers.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#1A4331]/5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#1A4331]/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#1A4331]" />
              </div>
              <h3 className="font-semibold text-[#1A4331]">Right for your season</h3>
            </div>
            <p className="text-sm text-[#5C665C]">
              Matched to your planting windows so you get the best harvest timing.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#1A4331]/5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#1A4331]/10 flex items-center justify-center">
                <Thermometer className="w-5 h-5 text-[#1A4331]" />
              </div>
              <h3 className="font-semibold text-[#1A4331]">Climate-matched</h3>
            </div>
            <p className="text-sm text-[#5C665C]">
              Suited to your local climate zone for reliable results.
            </p>
          </div>
        </div>
      </div>

      {/* Variety Detail Modal */}
      {selectedVariety && (
        <VarietyDetailModal
          variety={selectedVariety}
          onClose={() => setSelectedVariety(null)}
        />
      )}
    </Layout>
  );
}

function VarietyCard({ variety, onClick }) {
  const score = variety.recommendation_score || 0;
  const scoreColor = score >= 70 ? '#6B9F4A' : score >= 50 ? '#C9A85B' : '#8EAA97';

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm hover:shadow-md transition-all cursor-pointer group"
      data-testid={`variety-card-${variety.variety_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-2xl font-bold text-[#1A4331] group-hover:text-[#255C44] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {variety.variety_name}
            </h3>
            {variety.homegrown_recommended && (
              <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
            )}
          </div>
          <p className="text-sm text-[#5C665C]">{variety.crop_name}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div
            className="px-3 py-1 rounded-full text-xs font-bold"
            style={{ backgroundColor: `${scoreColor}22`, color: scoreColor }}
          >
            {score >= 70 ? 'Perfect match' : score >= 50 ? 'Good fit' : 'Consider'}
          </div>
          {variety.homegrown_rating && (
            <div className="flex items-center gap-1 text-xs text-[#5C665C]">
              <Star className="w-3 h-3 text-[#D4AF37]" fill="#D4AF37" />
              {variety.homegrown_rating}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-[#5C665C] leading-relaxed mb-4">
        {variety.description?.substring(0, 120)}...
      </p>

      {/* Why Recommended */}
      {variety.recommendation_reasons && variety.recommendation_reasons.length > 0 && (
        <div className="bg-[#F0F2EB] rounded-2xl p-3 mb-4">
          <p className="text-xs font-bold text-[#2D241E] mb-1.5">Why we recommend it:</p>
          <ul className="space-y-1">
            {variety.recommendation_reasons.map((reason, i) => (
              <li key={i} className="text-xs text-[#1A4331] flex items-start gap-1.5">
                <span className="text-[#6B9F4A] mt-0.5">✓</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#8EAA97]" />
          <span className="text-xs text-[#5C665C]">{variety.days_to_maturity} days</span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#8EAA97]" />
          <span className="text-xs text-[#5C665C]">{variety.difficulty_level}</span>
        </div>
      </div>

      {/* CTA */}
      <div className="flex items-center justify-between pt-3 border-t border-[#1A4331]/5">
        <button className="text-sm font-semibold text-[#1A4331] hover:text-[#255C44] transition-colors flex items-center gap-1">
          View variety <Info className="w-4 h-4" />
        </button>
        {variety.partner_links && variety.partner_links.length > 0 && (
          <a
            href={variety.partner_links[0].url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-sm font-semibold text-white bg-[#1A4331] hover:bg-[#255C44] px-4 py-2 rounded-full transition-colors flex items-center gap-1.5"
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            Buy seeds
          </a>
        )}
      </div>
    </div>
  );
}

function VarietyDetailModal({ variety, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-3xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="variety-detail-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {variety.variety_name}
              </h2>
              {variety.homegrown_recommended && (
                <Star className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
              )}
            </div>
            <p className="text-lg text-[#5C665C]">{variety.crop_name}</p>
            <p className="text-sm text-[#8EAA97] italic">{variety.species}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#8EAA97] hover:text-[#1A4331] transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Description */}
        <div className="mb-6">
          <p className="text-base text-[#2D241E] leading-relaxed">{variety.description}</p>
        </div>

        {/* Key Stats Grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#F0F2EB] rounded-2xl p-4">
            <p className="text-xs font-bold text-[#8EAA97] mb-1">Days to maturity</p>
            <p className="text-2xl font-bold text-[#1A4331]">{variety.days_to_maturity}</p>
          </div>
          <div className="bg-[#F0F2EB] rounded-2xl p-4">
            <p className="text-xs font-bold text-[#8EAA97] mb-1">Difficulty</p>
            <p className="text-2xl font-bold text-[#1A4331]">{variety.difficulty_level}</p>
          </div>
          <div className="bg-[#F0F2EB] rounded-2xl p-4">
            <p className="text-xs font-bold text-[#8EAA97] mb-1">Homegrown rating</p>
            <div className="flex items-center gap-1.5">
              <p className="text-2xl font-bold text-[#1A4331]">{variety.homegrown_rating}</p>
              <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
            </div>
          </div>
        </div>

        {/* Growing Details */}
        <div className="space-y-4 mb-6">
          <DetailSection icon={<Calendar className="w-5 h-5" />} title="Planting & Harvest">
            <p className="text-sm text-[#5C665C]">
              <strong>Sowing:</strong> {variety.sowing_method}
            </p>
            <p className="text-sm text-[#5C665C] mt-1">
              <strong>Harvest:</strong> {variety.harvest_indicators}
            </p>
          </DetailSection>

          <DetailSection icon={<TrendingUp className="w-5 h-5" />} title="Growing Tips">
            <p className="text-sm text-[#5C665C]">{variety.growing_tips}</p>
          </DetailSection>

          <DetailSection icon={<Thermometer className="w-5 h-5" />} title="Climate & Conditions">
            <div className="grid grid-cols-2 gap-2 text-sm text-[#5C665C]">
              <div><strong>Ideal temp:</strong> {variety.ideal_temp_range_c}°C</div>
              <div><strong>Frost tolerance:</strong> {variety.frost_tolerance}</div>
              <div><strong>Soil pH:</strong> {variety.soil_ph_range}</div>
              <div><strong>Spacing:</strong> {variety.plant_spacing_cm}cm</div>
            </div>
          </DetailSection>

          {variety.companion_plants && variety.companion_plants.length > 0 && (
            <DetailSection icon={<Sprout className="w-5 h-5" />} title="Companions">
              <div className="flex flex-wrap gap-2">
                {variety.companion_plants.map((c, i) => (
                  <span key={i} className="text-xs bg-[#6B9F4A]/10 text-[#1A4331] px-3 py-1 rounded-full border border-[#6B9F4A]/30">
                    {c}
                  </span>
                ))}
              </div>
            </DetailSection>
          )}
        </div>

        {/* Buy Seeds CTA */}
        {variety.partner_links && variety.partner_links.length > 0 && (
          <div className="bg-gradient-to-br from-[#1A4331] to-[#2D5F44] rounded-2xl p-6 text-white">
            <h3 className="text-lg font-bold mb-3">Where to get seeds</h3>
            <div className="flex flex-wrap gap-3 mb-4">
              {variety.partner_links.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/30 backdrop-blur px-4 py-2.5 rounded-full font-semibold transition-colors flex items-center gap-2"
                >
                  {link.retailer} <ExternalLink className="w-4 h-4" />
                </a>
              ))}
            </div>
            <details className="bg-white/10 rounded-xl p-3 backdrop-blur">
              <summary className="text-sm font-semibold cursor-pointer">
                Can&apos;t find this variety? 🔍
              </summary>
              <div className="mt-3 text-sm text-white/90 space-y-2">
                <p><strong>Local alternatives to look for:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Ask at your local garden center for &quot;similar varieties&quot;</li>
                  <li>Look for varieties with the same maturity days ({variety.days_to_maturity} days)</li>
                  <li>Check Kings Seeds, Egmont Seeds, or Mr Fothergill&apos;s catalogs</li>
                  <li>Join the Homegrown community to ask what others use</li>
                </ul>
                <p className="text-xs mt-2 italic">
                  💡 Tip: Most seed companies have similar varieties with different names. 
                  Ask for the same crop type and growing characteristics.
                </p>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailSection({ icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-[#1A4331]/5">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[#1A4331]">{icon}</div>
        <h3 className="font-semibold text-[#1A4331]">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

export default RecommendedVarieties;
